/**
 * TransactionList — oś czasu transakcji grupowana po dniach.
 * Header dnia: "Dziś" / "Wczoraj" / "29 marca". Pod nim wiersze.
 * Empty state: ikona + tekst.
 *
 * Faza 3 minimal: renderuje wszystkie naraz. Infinite scroll (30/page) dochodzi
 * w Faza 5 jak okaże się że dane prod są duże.
 */
import { useMemo } from 'react';
import { TransactionRow } from './TransactionRow';
import { Icons } from '../Icons';
import { formatDayHeader, groupTransactionsByDay } from '../../lib/utils';
import { useCategoriesStore } from '../../features/categories/categoriesStore';
import { useWalletsStore } from '../../features/wallets/walletsStore';
import type { Transaction } from '../../features/transactions/transactionsStore';

interface Props {
  transactions: Transaction[];
  onPick?: (tx: Transaction) => void;
}

export function TransactionList({ transactions, onPick }: Props) {
  const categories = useCategoriesStore((s) => s.categories);
  const wallets = useWalletsStore((s) => s.wallets);

  const walletNamesById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const w of wallets) map[w.id] = w.name;
    return map;
  }, [wallets]);

  const grouped = useMemo(() => groupTransactionsByDay(transactions), [transactions]);

  if (transactions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-slate-400 dark:text-slate-500">
        <div className="w-14 h-14 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-3">
          <Icons.Receipt size={28} className="opacity-50" />
        </div>
        <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Brak transakcji w tym miesiącu</p>
        <p className="text-xs mt-1">Tap "+" żeby dodać pierwszą</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {grouped.map((day) => (
        <section key={day.date.getTime()} className="space-y-1">
          <h3 className="text-[11px] uppercase tracking-wide font-bold text-slate-400 dark:text-slate-500 px-3">
            {formatDayHeader(day.date)}
          </h3>
          <div className="bg-white dark:bg-slate-800 rounded-card border border-slate-100 dark:border-slate-700 shadow-card overflow-hidden divide-y divide-slate-100 dark:divide-slate-700/60">
            {day.transactions.map((tx) => (
              <TransactionRow
                key={tx.id}
                tx={tx}
                category={categories.find((c) => c.id === tx.categoryId)}
                walletNamesById={walletNamesById}
                onClick={onPick}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
